import { ShieldAlert, Volume2, VolumeX, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CandidateWarning } from '../utils/browserWarnings';

const priority: Record<CandidateWarning['tone'], number> = { red: 4, orange: 3, yellow: 2, green: 1 };

export function WarningOverlay({ warnings, muted, onToggleMute }: { warnings: Array<CandidateWarning | undefined>; muted: boolean; onToggleMute: () => void }) {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => new Set());
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const warning = useMemo(() => warnings
    .filter((item): item is CandidateWarning => item !== undefined)
    .filter((item) => !dismissedIds.has(item.id))
    .sort((a, b) => priority[b.tone] - priority[a.tone] || Date.parse(a.timestamp) - Date.parse(b.timestamp))[0], [dismissedIds, warnings]);

  useEffect(() => {
    if (!warning) return;
    previousFocus.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDismissedIds((current) => new Set(current).add(warning.id));
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previousFocus.current?.focus();
    };
  }, [warning]);

  if (!warning) return null;
  const dismiss = () => setDismissedIds((current) => new Set(current).add(warning.id));
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/55 p-4 backdrop-blur-sm">
      <div ref={dialogRef} role="alertdialog" aria-modal="true" aria-labelledby="exam-warning-title" aria-describedby="exam-warning-message" tabIndex={-1} className="focus-ring w-full max-w-md rounded-md border border-amber-300/45 bg-slate-950 p-6 text-slate-50 shadow-2xl">
        <div className="flex items-start gap-4">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-amber-300/15 text-amber-200"><ShieldAlert className="h-6 w-6" /></div>
          <div className="min-w-0 flex-1">
            <h2 id="exam-warning-title" className="text-xl font-bold">{warning.title ?? 'Exam attention required'}</h2>
            <p id="exam-warning-message" className="mt-3 leading-7 text-slate-200">{warning.message}</p>
          </div>
          <button type="button" className="focus-ring rounded-md p-2 text-slate-300 hover:bg-white/10" onClick={dismiss} aria-label="Close warning"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-6 flex items-center justify-between gap-3">
          <button type="button" className="focus-ring rounded-md p-2 text-slate-300 hover:bg-white/10" onClick={onToggleMute} aria-label={muted ? 'Enable warning sounds' : 'Mute warning sounds'}>
            {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
          </button>
          <button type="button" className="focus-ring rounded-md bg-amber-300 px-5 py-2.5 font-bold text-slate-950 hover:bg-amber-200" onClick={dismiss}>Dismiss</button>
        </div>
      </div>
    </div>
  );
}
